import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./hooks/useAuth";

// Critical route loaded eagerly
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded public pages
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const TopicHubPage = lazy(() => import("./pages/TopicHubPage"));

// Lazy-loaded secondary pages
const EditorialTeamPage = lazy(() => import("./pages/EditorialTeamPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfUsePage = lazy(() => import("./pages/TermsOfUsePage"));

// Lazy-loaded admin pages
const AdminGuard = lazy(() => import("./components/AdminGuard"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const ArticlesList = lazy(() => import("./pages/admin/ArticlesList"));
const ArticleEditor = lazy(() => import("./pages/admin/ArticleEditor"));
const TrendingManager = lazy(() => import("./pages/admin/TrendingManager"));
const HeroEditor = lazy(() => import("./pages/admin/HeroEditor"));
const ImageGallery = lazy(() => import("./pages/admin/ImageGallery"));
const SEOAudit = lazy(() => import("./pages/admin/SEOAudit"));
const MarketDataAdmin = lazy(() => import("./pages/admin/MarketDataAdmin"));
const StorageCleanup = lazy(() => import("./pages/admin/StorageCleanup"));
const SitePages = lazy(() => import("./pages/admin/SitePages"));
const AutoEditorial = lazy(() => import("./pages/admin/AutoEditorial"));
const AuthorsManager = lazy(() => import("./pages/admin/AuthorsManager"));
const AnalyticsDashboard = lazy(() => import("./pages/admin/AnalyticsDashboard"));
const DuplicateDetector = lazy(() => import("./pages/admin/DuplicateDetector"));
const ContentCalendar = lazy(() => import("./pages/admin/ContentCalendar"));
const ActivityLog = lazy(() => import("./pages/admin/ActivityLog"));
const BrokenLinkChecker = lazy(() => import("./pages/admin/BrokenLinkChecker"));
const DatabaseExport = lazy(() => import("./pages/admin/DatabaseExport"));
const SiteHealth = lazy(() => import("./pages/admin/SiteHealth"));
const NewsletterGenerator = lazy(() => import("./pages/admin/NewsletterGenerator"));
const TagManager = lazy(() => import("./pages/admin/TagManager"));
const FixArticleImages = lazy(() => import("./pages/admin/FixArticleImages"));
const IndexingManager = lazy(() => import("./pages/admin/IndexingManager"));
const ContactsManager = lazy(() => import("./pages/admin/ContactsManager"));
const RSSFeedsManager = lazy(() => import("./pages/admin/RSSFeedsManager"));
const CronManager = lazy(() => import("./pages/admin/CronManager"));
const GSCDashboard = lazy(() => import("./pages/admin/GSCDashboard"));
const LighthouseDashboard = lazy(() => import("./pages/admin/LighthouseDashboard"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/article/:slug" element={<ArticlePage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/topic/:slug" element={<TopicHubPage />} />
              <Route path="/about" element={<EditorialTeamPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfUsePage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminGuard />}>
                <Route element={<AdminDashboard />}>
                  <Route index element={null} />
                  <Route path="articles" element={<ArticlesList />} />
                  <Route path="articles/:id" element={<ArticleEditor />} />
                  <Route path="trending" element={<TrendingManager />} />
                  <Route path="hero" element={<HeroEditor />} />
                  <Route path="authors" element={<AuthorsManager />} />
                  <Route path="images" element={<ImageGallery />} />
                  <Route path="seo" element={<SEOAudit />} />
                  <Route path="market-data" element={<MarketDataAdmin />} />
                  <Route path="auto-editorial" element={<AutoEditorial />} />
                  <Route path="storage" element={<StorageCleanup />} />
                  <Route path="pages" element={<SitePages />} />
                  <Route path="analytics" element={<AnalyticsDashboard />} />
                  <Route path="duplicates" element={<DuplicateDetector />} />
                  <Route path="calendar" element={<ContentCalendar />} />
                  <Route path="activity" element={<ActivityLog />} />
                  <Route path="broken-links" element={<BrokenLinkChecker />} />
                  <Route path="export" element={<DatabaseExport />} />
                  <Route path="health" element={<SiteHealth />} />
                  <Route path="newsletter" element={<NewsletterGenerator />} />
                  <Route path="tags" element={<TagManager />} />
                  <Route path="fix-images" element={<FixArticleImages />} />
                  <Route path="indexing" element={<IndexingManager />} />
                  <Route path="contacts" element={<ContactsManager />} />
                  <Route path="rss-feeds" element={<RSSFeedsManager />} />
                  <Route path="cron" element={<CronManager />} />
                  <Route path="gsc" element={<GSCDashboard />} />
                  <Route path="lighthouse" element={<LighthouseDashboard />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;